import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Zap } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/studio";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const r = await login(email, password);
    setLoading(false);
    if (r.ok) {
      toast.success("Welcome back");
      nav(next);
    } else {
      setError(r.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen">
      <div className="mx-auto flex min-h-screen max-w-[1200px] items-center px-6 py-12">
        <div className="grid w-full gap-0 md:grid-cols-2">
          {/* Left: brand */}
          <div className="border border-[#222] p-10">
            <Link to="/" className="font-display text-4xl tracking-widest">
              <span className="text-[#FF003C]">V</span>OLT<span className="text-[#FFE800]">.</span>MV
            </Link>
            <h1 className="mt-12 font-display text-6xl leading-none">
              WELCOME
              <br />
              <span className="text-[#FF003C]">BACK.</span>
            </h1>
            <p className="mt-6 max-w-sm text-[#c8c8c8]">
              Sign in to keep making cinematic AI music videos with Sora 2 + Claude Sonnet 5.
            </p>
            <div className="mt-10 font-mono text-xs uppercase tracking-widest text-[#808080]">
              // Studio · Gallery · Downloads
            </div>
          </div>

          {/* Right: form */}
          <div className="border border-l-0 border-[#222] bg-[#0A0A0B] p-10">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FFE800]">// LOG IN</div>
            <div className="mt-2 font-display text-4xl">Sign In</div>
            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
              <Field label="Email">
                <input
                  data-testid="login-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border border-[#222] bg-[#050505] p-3 font-mono text-sm text-white outline-none focus:border-[#FF003C]"
                  placeholder="you@example.com"
                />
              </Field>
              <Field label="Password">
                <input
                  data-testid="login-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border border-[#222] bg-[#050505] p-3 font-mono text-sm text-white outline-none focus:border-[#FF003C]"
                  placeholder="••••••••"
                />
              </Field>
              {error && (
                <div className="border border-[#FF003C] bg-[#FF003C]/10 p-3 font-mono text-xs text-[#FF003C]" data-testid="login-error">
                  {error}
                </div>
              )}
              <button data-testid="login-submit" type="submit" disabled={loading} className="btn-brutal w-full">
                {loading ? <Loader2 className="animate-spin" size={16} /> : <Zap size={16} />}
                Sign In
              </button>
            </form>
            <div className="mt-8 flex justify-between font-mono text-xs uppercase tracking-widest text-[#808080]">
              <Link to="/forgot-password" data-testid="login-forgot-link" className="hover:text-[#FFE800]">
                Forgot password?
              </Link>
              <Link to={`/register${next !== "/studio" ? `?next=${encodeURIComponent(next)}` : ""}`} data-testid="login-signup-link" className="hover:text-[#FFE800]">
                New here? Sign up →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="mb-1 font-mono text-[10px] uppercase tracking-widest text-[#808080]">{label}</div>
      {children}
    </label>
  );
}
